import * as React from 'react'
import {
    hasCachedUiLocaleTranslation,
    loadUiLocaleCatalog,
    normalizeUiLocaleLanguage,
    readCachedUiLocaleTranslation
} from '../utils/uiLocaleCatalog'

const translationCache = new Map()

const normalizeTexts = texts => [...new Set(
    (Array.isArray(texts) ? texts : [])
        .map(text => String(text ?? ''))
        .map(text => text.trim())
        .filter(Boolean)
)]

const buildCacheKey = (language, text) => `${language}::${text}`

const readCachedTranslations = (language, texts) =>
    Object.fromEntries(texts.map(text => {
        const cacheKey = buildCacheKey(language, text)
        return [
            text,
            language === 'en' ?
                text :
                (
                    translationCache.get(cacheKey) ??
                    readCachedUiLocaleTranslation(language, text) ??
                    text
                )
        ]
    }))

export default function useAutoTranslate(language, texts) {
    const normalizedLanguage = normalizeUiLocaleLanguage(language)
    const signature = normalizeTexts(texts).join('\u0001')
    const normalizedTexts = React.useMemo(
        () => (signature ? signature.split('\u0001') : []),
        [signature]
    )
    const [translations, setTranslations] = React.useState(() =>
        readCachedTranslations(normalizedLanguage, normalizedTexts)
    )

    React.useEffect(() => {
        const controller = new AbortController()
        let active = true

        const loadTranslations = async () => {
            try {
                if (normalizedLanguage !== 'en')
                    await loadUiLocaleCatalog(normalizedLanguage)

                if (!active)
                    return

                setTranslations(readCachedTranslations(normalizedLanguage, normalizedTexts))

                if (normalizedLanguage === 'en' || normalizedTexts.length === 0)
                    return

                const missingTexts = normalizedTexts.filter(text => {
                    const cacheKey = buildCacheKey(normalizedLanguage, text)
                    return !translationCache.has(cacheKey) &&
                        !hasCachedUiLocaleTranslation(normalizedLanguage, text)
                })

                if (missingTexts.length === 0)
                    return

                const response = await fetch('/translate-texts', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        targetLanguage: normalizedLanguage,
                        texts: missingTexts
                    }),
                    signal: controller.signal
                })

                if (!response.ok)
                    throw new Error(`Translation request failed (${response.status})`)

                const payload = await response.json()
                const translatedMap = payload?.translations && typeof payload.translations === 'object' ?
                    payload.translations :
                    {}

                Object.entries(translatedMap).forEach(([sourceText, translatedText]) => {
                    translationCache.set(
                        buildCacheKey(normalizedLanguage, sourceText),
                        String(translatedText ?? sourceText)
                    )
                })

                if (active)
                    setTranslations(readCachedTranslations(normalizedLanguage, normalizedTexts))
            }
            catch (error) {
                if (error?.name !== 'AbortError')
                    console.warn('[UiTranslation] Could not fetch translations:', error)
            }
        }

        loadTranslations()

        return () => {
            active = false
            controller.abort()
        }
    }, [normalizedLanguage, normalizedTexts, signature])

    return text => translations[String(text ?? '').trim()] ?? String(text ?? '')
}
